<script setup lang="ts">
import { ref, watch } from 'vue';

import KBtn from '@/components/btns/KBtn.vue';
import KRadioField from '@/components/fields/KRadioField.vue';
import KTextField from '@/components/fields/KTextField.vue';
import ReportGenerateDialogHeader from '@/components/headers/ReportGenerateDialogHeader.vue';
import PageDialogWrapper from '@/components/wrappers/PageDialogWrapper.vue';

export type SaveDraftMode = 'update' | 'new';

export interface Props {
  isConfirmLoading?: boolean;
  initialName?: string;
  isEditingDraft?: boolean;
  confirm?: (name: string, mode: SaveDraftMode) => Promise<unknown>;
}
export type DefProps = typeof props;
const props = withDefaults(defineProps<Props>(), {
  isConfirmLoading: false,
  initialName: '',
  isEditingDraft: false,
  confirm: undefined,
});

const emit = defineEmits<{
  cancelBtnClick: [event: Event];
  confirmBtnClick: [
    event: Event,
    name: string,
    mode: SaveDraftMode,
    props: DefProps,
  ];
}>();

const name = ref(props.initialName);
const mode = ref<SaveDraftMode>(props.isEditingDraft ? 'update' : 'new');
const showError = ref(false);

watch(
  () => props.initialName,
  (v) => {
    name.value = v;
    showError.value = false;
  },
);

watch(
  () => props.isEditingDraft,
  (isEditingDraft) => {
    mode.value = isEditingDraft ? 'update' : 'new';
  },
);

function handleCancelClick(event: Event): void {
  if (props.isConfirmLoading) return;
  emit('cancelBtnClick', event);
}

function handleConfirmClick(event: Event): void {
  if (props.isConfirmLoading) return;
  const trimmed = name.value.trim();
  if (!trimmed) {
    showError.value = true;
    return;
  }
  emit('confirmBtnClick', event, trimmed, mode.value, props);
}
</script>

<template>
  <PageDialogWrapper
    :class="$style.saveJobDraftDialog"
    data-testid="saveJobDraftDialog"
  >
    <template #header>
      <ReportGenerateDialogHeader @close-click="handleCancelClick">
        Save as draft
      </ReportGenerateDialogHeader>
    </template>
    <section :class="$style.main">
      <v-radio-group
        v-if="props.isEditingDraft"
        v-model="mode"
        :class="$style.radioGroup"
        :disabled="props.isConfirmLoading"
        hide-details
      >
        <KRadioField label="Save current draft" value="update" />
        <KRadioField label="Save as new draft" value="new" />
      </v-radio-group>
      <KTextField
        v-model="name"
        label="Draft name"
        size="md"
        :error-messages="
          showError && !name.trim() ? 'Please enter draft name' : ''
        "
        autofocus
        @keydown.enter="handleConfirmClick($event)"
      />
    </section>
    <template #footer>
      <menu :class="$style.dialogFooterMenuList">
        <li :class="$style.dialogFooterMenuListItem">
          <KBtn
            variant="outlined"
            priority="text"
            append-icon="mdi-close"
            :disabled="props.isConfirmLoading"
            @click="handleCancelClick"
          >
            Cancel
          </KBtn>
        </li>
        <li :class="$style.dialogFooterMenuListItem">
          <KBtn
            append-icon="mdi-content-save-outline"
            :loading="props.isConfirmLoading"
            @click="handleConfirmClick"
          >
            Save
          </KBtn>
        </li>
      </menu>
    </template>
  </PageDialogWrapper>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use '@/styles/variables';

.saveJobDraftDialog {
  width: variables.$reportGenerateDialogWidth;
}
  .main {
    padding-top: 0.5rem;
  }
  .radioGroup {
    margin-bottom: 0.5rem;
  }
  .dialogFooterMenuList {
    list-style: none;
    margin: 0;
    padding: 0;
    text-align: right;
  }
    .dialogFooterMenuListItem {
      display: inline;
    }
    .dialogFooterMenuListItem + .dialogFooterMenuListItem {
      margin-left: 1rem;
    }
</style>
